import { jwtDecode, type JwtPayload } from 'jwt-decode'

/**
 * Buffer time before token expiry to trigger refresh (5 minutes)
 */
export const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000

/**
 * Safely decode a JWT token
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeToken(token: string): JwtPayload | null {
  try {
    return jwtDecode<JwtPayload>(token)
  } catch {
    return null
  }
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token string (can be undefined)
 * @param bufferMs - Buffer time in ms to consider token expired early (default: 0)
 * @returns true if token is expired or invalid, false otherwise
 */
export function isTokenExpired(token: string | undefined, bufferMs = 0): boolean {
  if (!token) {
    return true
  }

  const decoded = decodeToken(token)
  if (!decoded?.exp) {
    // Token without exp claim is considered expired
    return true
  }

  const expiryTime = decoded.exp * 1000 // Convert to milliseconds
  const now = Date.now()

  return now >= expiryTime - bufferMs
}

/**
 * Check if token should be refreshed proactively
 * Returns true if token will expire within TOKEN_REFRESH_BUFFER_MS
 * @param token - JWT token string (can be undefined)
 * @returns true if token should be refreshed
 */
export function shouldRefreshToken(token: string | undefined): boolean {
  if (!token) {
    return false
  }

  // If token is already expired, don't try to refresh proactively
  if (isTokenExpired(token)) {
    return false
  }

  // Check if token will expire within buffer time
  return isTokenExpired(token, TOKEN_REFRESH_BUFFER_MS)
}

/**
 * Get time until token expires in milliseconds
 * @param token - JWT token string (can be undefined)
 * @returns Time to expiry in ms, or -1 if token is invalid/expired
 */
export function getTokenTimeToExpiry(token: string | undefined): number {
  if (!token) {
    return -1
  }

  const decoded = decodeToken(token)
  if (!decoded?.exp) {
    return -1
  }

  const expiryTime = decoded.exp * 1000
  const now = Date.now()
  const timeToExpiry = expiryTime - now

  return timeToExpiry > 0 ? timeToExpiry : -1
}
